import vertexShader from '../../shaders/cnc/cnc.vert';
import fragmentShader from '../../shaders/cnc/cnc.frag';
import 'three/examples/js/loaders/OBJLoader';

export default class SBPCNC extends THREE.Object3D {
  constructor () {
    super();

    this._t = 0;
  }

  setup ({ attributes, bin, offset, cncGeometry }) {
    const { positions, uvs } = attributes;
    const { width, depth, thickness } = bin;

    const count = positions.length / 3;
    const vertices = [];
    let minX = 1e8;
    let maxX = -1e8;
    let minY = 1e8;
    let maxY = -1e8;
    let minZ = 1e8;
    let maxZ = -1e8;
    for (let i = 0; i < count; i++) {
      const iv = i * 3;
      const x = positions[iv];
      const y = positions[iv + 1];
      const z = positions[iv + 2];
      const p = new THREE.Vector3(x, y, z);
      minX = Math.min(x, minX);
      maxX = Math.max(x, maxX);
      minY = Math.min(y, minY);
      maxY = Math.max(y, maxY);
      minZ = Math.min(z, minZ);
      maxZ = Math.max(z, maxZ);
      vertices.push(p);
    }

    const sizeX = maxX - minX;
    const sizeY = maxY - minY;
    const sizeZ = maxZ - minZ;

    const resolution = 2 << 13;
    const inv = 1 / resolution;
    const array = new Float32Array(resolution * 3);
    let current = 0;
    for (let i = 0; i < count; i++) {
      const iu = i * 2;
      const u = uvs[iu];
      const index = Math.floor(u * resolution);
      if (index >= current) {
        const prev = vertices[Math.max(0, i - 1)];
        const px = (prev.x - minX) / sizeX;
        const py = (prev.y - minY) / sizeY;
        const pz = (prev.z - minZ) / sizeZ;

        const n = vertices[i];
        const nx = (n.x - minX) / sizeX;
        const ny = (n.y - minY) / sizeY;
        const nz = (n.z - minZ) / sizeZ;
        const interval = index > current + 2 ? Math.max(0, 1 / (index - current - 1)) : 0;
        for (let j = current; j < index; j++) {
          const interp = (j - current) * interval; // 0.0 ~ 1.0
          const k = j * 3;
          const fx = this.lerp(px, nx, interp);
          const fy = this.lerp(py, ny, interp);
          const fz = this.lerp(pz, nz, interp);
          if (isNaN(fx)) {
            console.log(current, j, index, interp, fx, fy, fz);
          }
          array[k] = fx;
          array[k + 1] = fy;
          array[k + 2] = fz;
        }
        current = index;
      }
    }
    if (current <= resolution) {
      const l = vertices[count - 1];
      const lx = (l.x - minX) / sizeX;
      const ly = (l.y - minY) / sizeY;
      const lz = (l.z - minZ) / sizeZ;
      for (let i = current; i < resolution; i++) {
        const k = i * 3;
        array[k] = lx;
        array[k + 1] = ly;
        array[k + 2] = lz;
      }
    }

    const texture = new THREE.DataTexture(array, resolution, 1, THREE.RGBFormat, THREE.FloatType);
    texture.minFilter = texture.magFilter = THREE.LinearFilter;
    texture.needsUpdate = true;

    const diffuse = new THREE.Color(0x808080);
    const roughness = 0.52;
    const metalness = 0.05;

    const uniforms = THREE.UniformsUtils.merge([
      THREE.UniformsLib.common,
      THREE.UniformsLib.envmap,
      THREE.UniformsLib.normalmap,
      THREE.UniformsLib.roughnessmap,
      THREE.UniformsLib.lights,
      {
        diffuse: { value: diffuse },
        emissive: { value: new THREE.Color(0x000000) },
        roughness: { value: roughness },
        metalness: { value: metalness },
        envMapIntensity: { value: 1.0 },
        t: { value: this.t },
        positionTexture: { value: texture },
        bMin: { value: new THREE.Vector3(minX, minY, minZ) },
        bSize: { value: new THREE.Vector3(sizeX, sizeY, sizeZ) }
      }
    ]);

    const lights = true;
    const extensions = {};
    const defines = {
      STANDARD: '',
      // FLAT_SHADED: '',
      // USE_MAP: '',
      // USE_NORMALMAP: '',
      USE_ENVMAP: '',
      ENVMAP_TYPE_CUBE: '',
      ENVMAP_MODE_REFRACTION: '',
      USE_TANGENT: ''
      // OBJECTSPACE_NORMALMAP: '',
      // USE_UV: ''
    };

    const {
      xAxisTopGeometry, xAxisBottomGeometry,
      gantryYAxisGeometry, gantrySideTopGeometry, gantrySideBottomGeometry,
      spindleTipGeometry, spindleBaseGeometry
    } = cncGeometry;

    const xAxisMaterial = new THREE.MeshStandardMaterial({
      color: diffuse,
      roughness,
      metalness
    });

    const xAxisTop = new THREE.Mesh(xAxisTopGeometry, xAxisMaterial);
    const xAxisBottom = new THREE.Mesh(xAxisBottomGeometry, xAxisMaterial);

    const xAxisSize = new THREE.Vector3();
    const xAxisBB = xAxisTopGeometry.boundingBox;
    xAxisBB.getSize(xAxisSize);
    const xAxisScale = (width + offset.x) / xAxisSize.x;

    xAxisTop.scale.set(xAxisScale, 1, 1);
    xAxisBottom.scale.set(xAxisScale, 1, 1);
    const xAxisOffset = xAxisBB.min.x - xAxisBB.min.x * xAxisScale;

    const material = new THREE.ShaderMaterial({
      lights,
      extensions,
      defines,
      uniforms: THREE.UniformsUtils.merge([
        uniforms, {
          axis: { value: new THREE.Vector3(1, 0, 0) }
        }
      ]),
      vertexShader,
      fragmentShader
    });

    const gantryYAxis = new THREE.Mesh(gantryYAxisGeometry, material);

    const yAxisSize = new THREE.Vector3();
    const yAxisBB = gantryYAxisGeometry.boundingBox;
    yAxisBB.getSize(yAxisSize);
    const yAxisScale = (depth + offset.y) / yAxisSize.y;
    gantryYAxis.scale.set(1, yAxisScale, 1);
    gantryYAxis.position.set(0, yAxisBB.min.y - yAxisBB.min.y * yAxisScale, 0);

    const gantrySideTop = new THREE.Mesh(gantrySideTopGeometry, material);
    const gantrySideBottom = new THREE.Mesh(gantrySideBottomGeometry, material);

    const spindleTipMaterial = material.clone();
    spindleTipMaterial.uniforms.axis.value = new THREE.Vector3(1, 1, 1);
    const spindleTip = new THREE.Mesh(spindleTipGeometry, spindleTipMaterial);

    const spindleBaseMaterial = material.clone();
    spindleBaseMaterial.uniforms.axis.value = new THREE.Vector3(1, 1, 0);
    const spindleBase = new THREE.Mesh(spindleBaseGeometry, spindleBaseMaterial);

    xAxisTop.position.set(xAxisOffset, depth + offset.y - 55, 0);
    xAxisBottom.position.set(xAxisOffset, 0, 0);
    gantrySideTop.position.set(0, depth + offset.y + 100, 0);

    this.add(xAxisTop);
    this.add(xAxisBottom);
    this.add(gantryYAxis);
    this.add(gantrySideTop);
    this.add(gantrySideBottom);
    this.add(spindleTip);
    this.add(spindleBase);
  }

  lerp (x, y, t) {
    return x * (1 - t) + y * t;
  }

  get t () {
    return this._t;
  }

  set t (v) {
    this._t = v;
    this.children.forEach(mesh => {
      let uniforms = mesh.material.uniforms;
      if (uniforms !== undefined && uniforms.t !== undefined) {
        uniforms.t.value = this._t;
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/assets/javascripts/sbp/SBPCNC.js